// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { Homeserver } from "./api";
import type { Session } from "./types";

// passkey.ts drives the browser-side WebAuthn ceremonies against the homeserver's
// relying party (see internal/server/webauthn.go). The server speaks the standard
// WebAuthn JSON shapes with binary fields base64url-encoded; the browser API wants
// ArrayBuffers, so we transcode at the boundary. Login is discoverable (no username).

const hs = new Homeserver();

// passkeySupported reports whether this browser exposes the WebAuthn API. It is
// absent in the Capacitor WebView (origin http://localhost), so passkeys are a
// desktop / mobile-web feature.
export function passkeySupported(): boolean {
  return typeof PublicKeyCredential !== "undefined" && typeof navigator.credentials?.create === "function";
}

function b64urlEncode(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlToBytes(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOpts = any;

// registerPasskey binds a new credential to the logged-in user.
export async function registerPasskey(token: string, name: string): Promise<void> {
  const { options, session } = await hs.webauthnRegisterBegin(token);
  const pub: AnyOpts = (options as AnyOpts).publicKey;
  pub.challenge = b64urlToBytes(pub.challenge);
  pub.user.id = b64urlToBytes(pub.user.id);
  if (Array.isArray(pub.excludeCredentials)) {
    pub.excludeCredentials = pub.excludeCredentials.map((c: AnyOpts) => ({ ...c, id: b64urlToBytes(c.id) }));
  }
  const cred = (await navigator.credentials.create({ publicKey: pub })) as PublicKeyCredential | null;
  if (!cred) throw new Error("passkey creation was cancelled");
  const att = cred.response as AuthenticatorAttestationResponse;
  const body = {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: b64urlEncode(att.attestationObject),
      clientDataJSON: b64urlEncode(att.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
  await hs.webauthnRegisterFinish(token, session, name || "Passkey", body);
}

// loginPasskey performs a usernameless (discoverable) assertion and returns a session.
export async function loginPasskey(deviceName: string, deviceId: string): Promise<Session> {
  const { options, session } = await hs.webauthnLoginBegin();
  const pub: AnyOpts = (options as AnyOpts).publicKey;
  pub.challenge = b64urlToBytes(pub.challenge);
  if (Array.isArray(pub.allowCredentials)) {
    pub.allowCredentials = pub.allowCredentials.map((c: AnyOpts) => ({ ...c, id: b64urlToBytes(c.id) }));
  }
  const cred = (await navigator.credentials.get({ publicKey: pub })) as PublicKeyCredential | null;
  if (!cred) throw new Error("passkey sign-in was cancelled");
  const asr = cred.response as AuthenticatorAssertionResponse;
  const body = {
    id: cred.id,
    rawId: b64urlEncode(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: b64urlEncode(asr.authenticatorData),
      clientDataJSON: b64urlEncode(asr.clientDataJSON),
      signature: b64urlEncode(asr.signature),
      userHandle: asr.userHandle ? b64urlEncode(asr.userHandle) : "",
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
  return hs.webauthnLoginFinish(session, deviceName, deviceId, body);
}
